import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';

// Layouts
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Auth guards
const RequireAuth = ({ children }) => {
  const token = localStorage.getItem('user_token');
  return token ? children : <Navigate to="/login" replace />;
};
const RequireAdmin = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  return token ? children : <Navigate to="/admin/login" replace />;
};

// Pages - User
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Authorizations = lazy(() => import('./pages/Authorizations'));
const UserCenter = lazy(() => import('./pages/UserCenter'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const RechargeGuide = lazy(() => import('./pages/RechargeGuide'));
const Contact = lazy(() => import('./pages/Contact'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));

// Pages - Admin
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminAuthorizations = lazy(() => import('./pages/admin/AdminAuthorizations'));
const AdminFinance = lazy(() => import('./pages/admin/AdminFinance'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminAgents = lazy(() => import('./pages/admin/AdminAgents'));
const AdminSharing = lazy(() => import('./pages/admin/AdminSharing'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminBalanceLogs = lazy(() => import('./pages/admin/AdminBalanceLogs'));

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#00f3ff',
          colorBgContainer: 'rgba(20,25,35,0.45)',
          colorBorder: 'rgba(255,255,255,0.08)',
          colorText: '#ffffff',
          colorTextSecondary: '#8b9bb4',
          borderRadius: 8,
          fontFamily: "'Inter', 'Noto Sans SC', -apple-system, BlinkMacSystemFont, sans-serif",
        },
      }}
    >
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* 用户端路由 */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/recharge-guide" element={<RechargeGuide />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/downloads" element={<RequireAuth><Downloads /></RequireAuth>} />
              <Route path="/authorizations" element={<RequireAuth><Authorizations /></RequireAuth>} />
              <Route path="/user" element={<RequireAuth><UserCenter /></RequireAuth>} />
            </Route>

            {/* 独立登录页 */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* 管理端路由 */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="authorizations" element={<AdminAuthorizations />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="balance-logs" element={<AdminBalanceLogs />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="agents" element={<AdminAgents />} />
              <Route path="sharing" element={<AdminSharing />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="reports" element={<AdminReports />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
